import { button } from "../../design/components.js";
import type { AvailabilityInput } from "./help-publish.js";

export interface HelperModeCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  homeCell: string; // res-8 working cell (availability geo anchor)
  parentCell: string; // res-7 parent (availability.cells, per lexicon x-beacon-h3-res:7)
  tierCaption: string; // e.g. "ESTABLISHED · VOUCHED BY 3"
  nowMs: number; // base for TTL computation (supplied by app.ts — no clock read here)
  onRegister: (input: AvailabilityInput) => Promise<void>;
}

const NEED_TAGS = ["medical", "transport", "shelter", "supplies", "hands"] as const;
const TTL_HOURS: Record<string, number> = { today: 12, week: 168, incident: 72 };

/** Helper-mode card (GUI §4): toggle "I can help" → need-type tags + TTL chip + coarse area
 *  + trust-tier caption. On register, emits a real helper.availability via onRegister. */
export function helperModeCard(ctx: HelperModeCtx): HTMLElement {
  const t = ctx.t;
  const root = document.createElement("section");
  root.className = "helper-mode card";
  root.setAttribute("data-helper-mode", "");

  let expanded = false;
  const tags = new Set<string>();
  let ttl: keyof typeof TTL_HOURS = "today";

  const render = () => {
    root.replaceChildren();

    const header = document.createElement("div");
    header.className = "helper-mode__header";
    const intro = document.createElement("p");
    intro.className = "bx-body";
    intro.textContent = t("help.helper.intro");
    const toggle = button({
      label: t("help.helper.toggle"),
      variant: expanded ? "primary" : "secondary",
      onClick: () => { expanded = !expanded; render(); },
    });
    toggle.setAttribute("data-action", "toggle-helper");
    header.append(intro, toggle);
    root.append(header);

    const caption = document.createElement("p");
    caption.className = "bx-microlabel helper-mode__tier";
    caption.textContent = ctx.tierCaption;
    root.append(caption);

    if (!expanded) return;

    // Need-type tags
    const tagsLabel = document.createElement("div");
    tagsLabel.className = "bx-microlabel";
    tagsLabel.textContent = t("help.helper.tagsLabel");
    const tagRow = document.createElement("div");
    tagRow.className = "helper-mode__tags";
    tagRow.setAttribute("data-helper-tags", "");
    for (const tag of NEED_TAGS) {
      const b = button({
        label: t(`need.${tag}`),
        variant: tags.has(tag) ? "primary" : "secondary",
        onClick: () => { tags.has(tag) ? tags.delete(tag) : tags.add(tag); render(); },
      });
      b.setAttribute("data-tag", tag);
      tagRow.append(b);
    }
    root.append(tagsLabel, tagRow);

    // TTL chips
    const ttlLabel = document.createElement("div");
    ttlLabel.className = "bx-microlabel";
    ttlLabel.textContent = t("help.helper.ttlLabel");
    const ttlRow = document.createElement("div");
    ttlRow.className = "helper-mode__ttl";
    for (const k of ["today", "week", "incident"] as const) {
      const b = button({
        label: t(`help.helper.ttl.${k}`),
        variant: ttl === k ? "primary" : "secondary",
        onClick: () => { ttl = k; render(); },
      });
      b.setAttribute("data-ttl", k);
      ttlRow.append(b);
    }
    root.append(ttlLabel, ttlRow);

    const area = document.createElement("p");
    area.className = "bx-microlabel";
    area.textContent = t("help.helper.area");
    root.append(area);

    const register = button({
      label: t("help.helper.register"),
      variant: "primary",
      onClick: () => {
        const untilIso = new Date(ctx.nowMs + TTL_HOURS[ttl] * 3_600_000).toISOString();
        void ctx.onRegister({
          tags: [...tags], cells: [ctx.parentCell], untilIso, autoAccept: false, cell: ctx.homeCell,
        }).then(() => {
          const done = document.createElement("p");
          done.className = "bx-body helper-mode__done";
          done.textContent = t("help.helper.registered");
          root.append(done);
        });
      },
    });
    register.setAttribute("data-action", "register-availability");
    (register as HTMLButtonElement).disabled = tags.size === 0;
    root.append(register);
  };

  render();
  return root;
}
